
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import { LoadingStatus } from '@/types/common';

import ResponseHandlerModule from '@/store/modules/responseHandler';

import {
    // slots
    getMonthSlots,
    // consultations
    createConsultation,
    // schedule
    updateScheduleTemplate,
    updateScheduleDate,
    removeScheduleDate,
    // vacation
    createVacation,
    updateVacation,
    removeVacation,
} from '@/api/calendar';

import { CalendarConsultation } from '@/types/models/calendar';

import { discardTime, createDateRangeChecker, createDatetimeRangeChecker } from '@/utils/date';
import { filterEntries } from '@/utils/object';

import { ScheduleSetForm, ScheduleEditForm } from './forms/schedule/types';
import { ISOCalendarDate, ISODateRange } from '@/utils/date/types';
import { SlotsClinicMap, SlotsMap, SlotType, VacationSlot } from './types';
import { Maybe } from '@/types/common';
import { ConsultationRequest } from '@/types/consultations';

@Component({})
export default class CalendarRepository extends Vue {
    @Prop() doctorId!: number
    @Prop() selectedRequest!: Maybe<ConsultationRequest>;

    slots: SlotsMap = {};
    slotsClinic: SlotsClinicMap = {};
    slotsCustom = {};
    status = LoadingStatus.Idle;
    lastFetchRange: Maybe<ISODateRange> = null

    render() {
        return this.$scopedSlots.default?.({
            slots: this.slots,
            slotsClinic: this.slotsClinic,
            slotsCustom: this.slotsCustom,
            status: this.status,
            fetch: this.fetchSlots,
            create: this.createConsultation,

            schedule: {
                updateTemplate: this.updateTemplateSchedule,
                update: this.updateScheduleDate,
                remove: this.removeScheduleDate
            },

            vacation: {
                create: this.createVacation,
                update: this.updateVacation,
                remove: this.removeVacation,
            }
        })
    }

    @Watch('doctorId')
    refetchSlots() {
        this.slots = {};

        if (this.doctorId && this.lastFetchRange) {
            this.fetchSlots(this.lastFetchRange);
        }
    }

    async fetchSlots(range: ISODateRange) {
        this.lastFetchRange = range;

        try {
            let [slots, slotsClinic, slotsCustom] = await getMonthSlots({
                doctorId: Number(this.doctorId),
                range,
            });

            let entries = Object.entries(slots);
            if (this.selectedRequest) {
                const selectedRequestStart = this.selectedRequest.time?.start;
                const selectedRequestEnd = this.selectedRequest.time?.end;

                entries = entries.map(entry => {
                    const [slotsEntryKey, slotEntryValue] = entry;
                    const newEntryValue = slotEntryValue.map(slot => {
                        if (
                            slot.start === selectedRequestStart &&
                            slot.end === selectedRequestEnd
                        ) {
                            slot.type = SlotType.PatientChosen;
                        }
                        
                        return slot;
                    });

                    return [slotsEntryKey, newEntryValue];
                });


                slots = Object.fromEntries(entries);
            }

            this.slots = {
                ...this.slots,
                ...slots
            }

            this.slotsClinic = {
                ...this.slotsClinic,
                ...slotsClinic,
            }

            this.slotsCustom = {
                ...this.slotsCustom,
                ...slotsCustom,
            }
        } catch (clientError: any) {
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    async updateTemplateSchedule(schedule: ScheduleSetForm) {
        schedule.schedule = schedule.schedule.filter(day => day.clinic !== null);
        
        try {
            await updateScheduleTemplate(this.doctorId, schedule);
            
            // refetch newly created slots
            const range: ISODateRange = {
                start: schedule.from,
                end: schedule.to
            }
            await this.fetchSlots(range)
        } catch (clientError: any) {
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    async updateScheduleDate(schedule: ScheduleEditForm) {
        try {
            const slots = await updateScheduleDate(this.doctorId, schedule);

            Vue.set(this.slots, schedule.date, slots);
        } catch (clientError: any) {
            const error = clientError.response?.data ?? clientError;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    async removeScheduleDate(date: ISOCalendarDate) {
        try {
            await removeScheduleDate(this.doctorId, date);

            const {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                [date]: _removedDate,
                ...slots
            } = this.slots;

            this.slots = slots;
        } catch (clientError: any) {
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    async createConsultation(consultation: CalendarConsultation) {
        try {
            const createdSlot = await createConsultation(this.doctorId, consultation);

            const date = discardTime(createdSlot.start)
            const isInRange = createDatetimeRangeChecker({
                start: createdSlot.start,
                end: createdSlot.end,
            })

            const oldSlots = this.slots[date];
            // remove empty slots-placeholder
            const slots = oldSlots.filter(slot => !isInRange(slot.start));
            // add new slot (order doesn't matter)
            slots.push(createdSlot);
            
            Vue.set(this.slots, date, slots);
        } catch (clientError: any) {
            ResponseHandlerModule.cancelRedirectToNotFound();
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    // vacation

    async createVacation(dates: ISODateRange) {
        try {
            await createVacation(this.doctorId, dates);

            // refetch dates
            await this.fetchSlots(dates)

        } catch (clientError: any) {
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    async updateVacation({ vacation, dates }: { vacation: VacationSlot; dates: ISODateRange }) {
        try {
            await updateVacation(this.doctorId, vacation.vacationId, dates);

            // remove old dates
            const isInRange = createDateRangeChecker(vacation.dates);
            this.slots = filterEntries(this.slots, (_, date) => !isInRange(date));
            
            // refetch new dates
            await this.fetchSlots(dates)
        } catch (clientError: any) {
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }

    async removeVacation(vacation: VacationSlot) {
        try {
            await removeVacation(this.doctorId, vacation.vacationId);

            const isInRange = createDateRangeChecker(vacation.dates);
            this.slots = filterEntries(this.slots, (_, date) => !isInRange(date));
        } catch (clientError: any) {
            const error = clientError.response.data;
            const errorMessage = error.message;

            ResponseHandlerModule.showNotify({ message: errorMessage, type: 'fail' });
        }
    }
}
